import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi, uploadFileToSignedUrl } from '../lib/api.ts';
import {
  describeSend,
  firstSendableId,
  isManualSendable,
  isResendBlocked,
  optionLabel,
  type SendOption,
} from './sendQuestionnaire.ts';
import { downloadSignedUrl } from '../lib/download.ts';
import { Avatar } from '../components/Avatar.tsx';
import { Btn } from '../components/Btn.tsx';
import { ConfirmModal } from '../components/ConfirmModal.tsx';
import { CenteredSpinner } from '../components/Spinner.tsx';
import { CompanyStatusBadge, SubmissionStatusBadge } from '../components/StatusBadge.tsx';
import { ProgressBar } from '../components/ProgressBar.tsx';
import { ErrorMessage } from '../components/ErrorMessage.tsx';
import { ExpiryChip } from '../components/ExpiryChip.tsx';
import { Modal } from '../components/Modal.tsx';
import { NavIcon } from '../components/NavIcon.tsx';
import { Field, Select, TextInput } from '../components/Field.tsx';
import { FileDrop } from '../components/FileDrop.tsx';
import { DateField } from '../components/DateField.tsx';
import { DOCUMENT_ACCEPT } from '../lib/fileTypes.ts';
import { MultiSelect } from '../components/MultiSelect.tsx';
import { StringListEditor } from '../components/StringListEditor.tsx';
import { fromDateInput } from '../lib/expiry.ts';
import { effectiveDocStatus } from '../lib/submissionStatus.ts';
import { hasExpiredLiveFile } from '../lib/documentVersions.ts';
import { useAbnVerification } from '../lib/useAbnVerification.ts';
import { useAutoFillField } from '../lib/autoFillField.ts';
import { AbnStatusIndicator, AbnVerifiedBadge } from '../components/AbnStatus.tsx';
import { usePortalSettings } from '../lib/portalSettings.tsx';
import {
  clusterRegionOptions,
  companyInfoLabel,
  contractorTypeOptions,
  stationOptions,
} from '../lib/companyInfo.ts';
import type {
  AdminQuestionnaire,
  AdminSubmission,
  Company,
  CompanyDocument,
  Question,
  QuestionCategory,
  Submission,
  SubmissionFile,
} from '../lib/types.ts';
import { computeProgress, type CompletionReason } from '../lib/visibility.ts';
import { outstandingActionCount } from '../lib/outstandingActions.ts';
import {
  buildCompanyDetailTabs,
  questionsForProgress,
  type CategorySection,
  type ComplianceDocRow,
  type QuestionnaireGroup,
} from '../lib/companyDetailTabs.ts';
import {
  questionnaireProgress,
  reviewProgress,
  type QuestionnaireProgress,
  type ReviewProgress,
} from '../lib/companyDetailProgress.ts';
import { instanceOptionLabel } from '../lib/instanceOption.ts';
import { complianceDocName } from '../lib/complianceUploads.ts';
import { answerOptionPills, type AnswerOptionPill } from '../lib/answerOptions.ts';
import { useReviewMutation } from './useReviewMutation.ts';
import { Tabs, type TabItem } from '../components/Tabs.tsx';
import { CommentModal } from './CommentModal.tsx';
import { awaitingAdminReply, awaitingReplyCount } from './awaitingReply.ts';
import { NotCompliantModal } from './NotCompliantModal.tsx';
import { DocumentReviewTable } from './DocumentReviewTable.tsx';
import { ComplianceDocStats } from './ComplianceDocStats.tsx';
import { CompanyActivityTab } from './CompanyActivityTab.tsx';
import { isAwaitingAdmin, reviewTurn, ADMIN_REVIEW_LABEL } from '../lib/reviewState.ts';
import { frozenApprovedDoc } from '../lib/frozenApprovedDoc.ts';
import { abnNeedsAction } from '../lib/abnAction.ts';
import { selectEffectiveQuestions, type SnapshotRow } from '@l2u/shared/effectiveQuestions';
import { ExportQuestionnaireButton } from './ExportQuestionnaireButton.tsx';
import { FileHistoryModal } from './FileHistoryModal.tsx';

// Synthetic keys for the fixed tabs. Namespaced with underscores so they can
// never collide with a real questionnaire UUID.
const COMPANY_INFO_TAB = '__company_info__';
const COMPLIANCE_DOCS_TAB = '__compliance_docs__';
const ACTIVITY_TAB = '__activity_log__';
const QUESTIONNAIRES_TAB = '__questionnaires__';
const ADDITIONAL_TAB = '__additional_questions__';

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-AU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export function AdminCompanyDetail() {
  const { id = '' } = useParams();
  const location = useLocation();
  // "← Vendors" always returns to the vendors list (matching its label) and
  // carries back the page + filters the admin left off on — the list's row link
  // stashes them in history state (they also live in the list URL). Reached from
  // elsewhere (Dashboard, Expiring Documents), it lands on the unfiltered list.
  const backToVendors = `/admin/companies${
    (location.state as { vendorsListSearch?: string } | null)?.vendorsListSearch ?? ''
  }`;
  const qc = useQueryClient();
  const [notifiedAt, setNotifiedAt] = useState<number | null>(null);

  const companyQuery = useQuery({
    queryKey: ['company', id],
    queryFn: () => adminApi.get<{ company: Company }>(`/api/companies/${id}`),
    enabled: !!id,
  });
  const submissionsQuery = useQuery({
    queryKey: ['company', id, 'submissions'],
    queryFn: () =>
      adminApi.get<{ submissions: AdminSubmission[]; question_snapshot: SnapshotRow[] }>(
        `/api/companies/${id}/submissions`,
      ),
    enabled: !!id,
  });
  const questionsQuery = useQuery({
    queryKey: ['questions'],
    queryFn: () => adminApi.get<{ questions: Question[] }>('/api/questions'),
  });
  const documentsQuery = useQuery({
    queryKey: ['documents'],
    queryFn: () => adminApi.get<{ documents: CompanyDocument[] }>('/api/documents'),
  });
  const categoriesQuery = useQuery({
    queryKey: ['question-categories', 'all'],
    queryFn: () =>
      adminApi.get<{ categories: QuestionCategory[] }>('/api/question-categories?include_archived=1'),
  });
  const questionnairesQuery = useQuery({
    queryKey: ['company', id, 'questionnaires'],
    queryFn: () =>
      adminApi.get<{ questionnaires: AdminQuestionnaire[] }>(`/api/companies/${id}/questionnaires`),
    enabled: !!id,
  });

  const [inviteSentOpen, setInviteSentOpen] = useState(false);
  const inviteMutation = useMutation({
    mutationFn: () => adminApi.post(`/api/companies/${id}/invite`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['company', id] });
      setInviteSentOpen(true);
    },
  });

  const [confirmOpen, setConfirmOpen] = useState(false);
  const deactivateMutation = useMutation({
    mutationFn: () => adminApi.post<{ company: Company }>(`/api/companies/${id}/deactivate`),
    onSuccess: () => {
      setConfirmOpen(false);
      void qc.invalidateQueries({ queryKey: ['company', id] });
      void qc.invalidateQueries({ queryKey: ['companies'] });
    },
  });
  const reactivateMutation = useMutation({
    mutationFn: () => adminApi.post<{ company: Company }>(`/api/companies/${id}/reactivate`),
    onSuccess: () => {
      setConfirmOpen(false);
      void qc.invalidateQueries({ queryKey: ['company', id] });
      void qc.invalidateQueries({ queryKey: ['companies'] });
    },
  });

  const pendingChangesQuery = useQuery({
    queryKey: ['company', id, 'pending-changes'],
    queryFn: () => adminApi.get<{ count: number }>(`/api/companies/${id}/notify-changes`),
    enabled: !!id,
  });

  const notifyChangesMutation = useMutation({
    mutationFn: () =>
      adminApi.post<{ sent: boolean; count: number; to?: string }>(
        `/api/companies/${id}/notify-changes`,
      ),
    onSuccess: () => {
      setNotifiedAt(Date.now());
      void qc.invalidateQueries({ queryKey: ['company', id, 'pending-changes'] });
    },
  });

  const pendingChangesCount = pendingChangesQuery.data?.count ?? 0;

  const documents = documentsQuery.data?.documents ?? [];
  const docMap = useMemo(() => {
    const m = new Map<string, CompanyDocument>();
    for (const d of documents) m.set(d.id, d);
    return m;
  }, [documents]);
  const documentName = (docId: string) => docMap.get(docId)?.name ?? docId.slice(0, 8);

  const submissions = submissionsQuery.data?.submissions ?? [];
  const awaitingReplyTotal = awaitingReplyCount(submissions);
  const questions = questionsQuery.data?.questions ?? [];
  const questionSnapshot = submissionsQuery.data?.question_snapshot ?? [];
  // Freeze: for a submitted questionnaire show only its snapshotted questions
  // (incl. retired ones, for fading); unsubmitted questionnaires show the live
  // active set. Same rule the vendor API applies server-side.
  const effectiveQuestions = useMemo(
    () => selectEffectiveQuestions(questions, questionSnapshot),
    [questions, questionSnapshot],
  );
  const subByQ = useMemo(() => {
    const m = new Map<string, AdminSubmission>();
    for (const s of submissions) m.set(s.question_id, s);
    return m;
  }, [submissions]);

  // Question ids whose submission is approved (marked compliant) — drives which
  // compliance docs are mirrored into the Compliance Documents tab. They also
  // stay inline in their questionnaire (mirrors isApprovedComplianceDoc on the
  // vendor side).
  const approvedIds = useMemo(
    () => new Set(submissions.filter((s) => s.status === 'approved').map((s) => s.question_id)),
    [submissions],
  );

  // Question ids in a renewal cycle (vendor replaced an expired approved doc;
  // admin owes a re-verification) — pinned in the Compliance Documents tab
  // alongside the approved ones, where the re-review buttons render.
  const reverifyIds = useMemo(
    () =>
      new Set(
        submissions.filter((s) => s.reverify_requested_at != null).map((s) => s.question_id),
      ),
    [submissions],
  );

  const questionById = useMemo(() => new Map(questions.map((x) => [x.id, x])), [questions]);

  const categories = categoriesQuery.data?.categories ?? [];
  const questionnaires = questionnairesQuery.data?.questionnaires ?? [];

  // Progress counts only questions the vendor can actually be asked —
  // applicable questionnaires plus orphans — matching the vendor portal's
  // server-side computation (see questionsForProgress).
  const progressQuestions = useMemo(
    () => questionsForProgress(questionnaires, categories, effectiveQuestions),
    [questionnaires, categories, effectiveQuestions],
  );
  const progress = useMemo(
    () => computeProgress(progressQuestions, submissions as unknown as Submission[]),
    [progressQuestions, submissions],
  );

  const answeredIds = useMemo(() => new Set(submissions.map((s) => s.question_id)), [submissions]);

  // Tab model: applicable questionnaires become tabs (category sections,
  // including compliance docs shown inline), retired-but-answered
  // questionnaires collapse into one Questionnaires tab bucket, and approved
  // compliance docs are additionally mirrored into the Compliance Documents rows.
  const tabModel = useMemo(
    () =>
      buildCompanyDetailTabs(
        questionnaires,
        categories,
        effectiveQuestions,
        progress.visibleIds,
        answeredIds,
        approvedIds,
        reverifyIds,
      ),
    [
      questionnaires,
      categories,
      effectiveQuestions,
      progress.visibleIds,
      answeredIds,
      approvedIds,
      reverifyIds,
    ],
  );
  const [activeTab, setActiveTab] = useState<string>(COMPANY_INFO_TAB);
  // Which grouped questionnaire is shown in the consolidated "Questionnaires"
  // tab. '' = none selected → no questions shown until the admin picks one.
  const [selectedGroupedId, setSelectedGroupedId] = useState<string>('');
  const [uploadDocOpen, setUploadDocOpen] = useState(false);
  const [sendQOpen, setSendQOpen] = useState(false);

  if (
    companyQuery.isLoading ||
    submissionsQuery.isLoading ||
    questionsQuery.isLoading ||
    categoriesQuery.isLoading ||
    documentsQuery.isLoading ||
    questionnairesQuery.isLoading
  ) {
    return <CenteredSpinner />;
  }
  const company = companyQuery.data?.company;
  if (!company) {
    return <ErrorMessage error={new Error('Vendor not found')} />;
  }

  const isDeactivated = company.status === 'deactivated';

  // Manual+active questionnaires offered in the Send dialog, each flagged
  // compliant when the vendor's submissions for it are all approved — the same
  // signal as the per-questionnaire "Compliant" pill (reviewTurn). Compliant
  // ones are shown but disabled, so they cannot be resent.
  const sendOptions: SendOption[] = questionnaires.filter(isManualSendable).map((q) => {
    // Gather over VISIBLE questions only, so this compliance signal matches the
    // per-questionnaire "Compliant" pill exactly (one source of truth).
    const subs = effectiveQuestions
      .filter((eq) => eq.questionnaire_id === q.id && progress.visibleIds.has(eq.id))
      .map((eq) => subByQ.get(eq.id))
      .filter((s): s is AdminSubmission => Boolean(s));
    return {
      id: q.id,
      name: q.name,
      assigned: q.assigned,
      sent_at: q.sent_at,
      compliant: reviewTurn(subs) === 'compliant',
    };
  });

  // "Vendor information" leads the strip, then one tab per applicable
  // questionnaire (own-tab), then Questionnaires (consolidated grouped +
  // orphans), then Compliance Documents, then Activity log.
  const hasGrouped = tabModel.groupedTab.length > 0;
  const hasOrphans = tabModel.orphanQuestions.length > 0;

  // Per-questionnaire dropdown badges use the same outstanding-actions metric
  // as every other tab badge (pending/rejected submissions + required, visible,
  // unanswered questions), so the dropdown items, their tab total, and the
  // sibling own-tab/Compliance badges all count the same thing.
  const outstandingCount = (qs: Question[]) =>
    outstandingActionCount(qs, progress.completionByQuestion, subByQ, { skipFrozen: true });

  const questionnaireMenuItems = [
    ...tabModel.groupedTab.map((g) => ({
      key: g.questionnaire.id,
      label: g.questionnaire.name,
      badge: outstandingCount(groupQuestions(g)),
    })),
    ...(hasOrphans
      ? [
          {
            key: ADDITIONAL_TAB,
            label: 'Additional questions',
            badge: outstandingCount(tabModel.orphanQuestions),
          },
        ]
      : []),
  ];

  const tabItems: TabItem[] = [
    { key: COMPANY_INFO_TAB, label: 'Vendor information' },
    ...tabModel.questionnaireTabs.map((g) => ({
      key: g.questionnaire.id,
      label: g.questionnaire.name,
      badge: outstandingActionCount(groupQuestions(g), progress.completionByQuestion, subByQ, {
        skipFrozen: true,
      }),
    })),
    ...(hasGrouped || hasOrphans
      ? [
          {
            key: QUESTIONNAIRES_TAB,
            label: 'Questionnaires',
            // The tab total is the sum of the per-questionnaire item badges
            // above, so the closed-tab count always matches the breakdown the
            // dropdown reveals. (`?? 0` guards a future badge-less item from
            // poisoning the sum with NaN.)
            badge: questionnaireMenuItems.reduce((sum, item) => sum + (item.badge ?? 0), 0),
            menu: {
              items: questionnaireMenuItems,
              selectedKey: selectedGroupedId,
              // Selecting a questionnaire is what navigates into the
              // Questionnaires panel — clicking the tab alone only opens this
              // dropdown (see MenuTab.handleClick).
              onSelectItem: (key: string) => {
                setSelectedGroupedId(key);
                setActiveTab(QUESTIONNAIRES_TAB);
              },
            },
          },
        ]
      : []),
    {
      key: COMPLIANCE_DOCS_TAB,
      label: 'Compliance Documents',
      badge: outstandingActionCount(
        tabModel.complianceDocs.map((r) => r.question),
        progress.completionByQuestion,
        subByQ,
      ),
    },
    { key: ACTIVITY_TAB, label: 'Activity log' },
  ];
  // Fall back to Vendor information when the stored key no longer matches a
  // tab (e.g. a questionnaire was deactivated between refetches).
  const activeKey = tabItems.some((t) => t.key === activeTab) ? activeTab : COMPANY_INFO_TAB;
  const activeGroup = tabModel.questionnaireTabs.find((g) => g.questionnaire.id === activeKey);
  // Resolve the dropdown selection for the consolidated "Questionnaires" tab.
  // Returns undefined when nothing is selected or the id went stale between
  // refetches (e.g. the questionnaire was deactivated) → empty state.
  const selectedGroupedGroup = tabModel.groupedTab.find(
    (g) => g.questionnaire.id === selectedGroupedId,
  );

  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <Link to={backToVendors} className="text-sm text-slate-500 hover:text-slate-700">
          ← Vendors
        </Link>

        {/* Header row: identity on the left, primary actions aligned on the
            right of the same row. */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          {/* Identity: avatar, then the company name with its status beside it
              and the primary contact + email on the line underneath. */}
          <div className="flex items-start gap-4">
            <Avatar name={company.company_name} size={56} />
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-3">
                <h1 className="text-2xl font-semibold text-slate-900">{company.company_name}</h1>
                <CompanyStatusBadge status={company.status} />
                {abnNeedsAction(company.abn_verification_status) && (
                  <span className="inline-flex items-center rounded-full bg-red-50 px-2.5 py-0.5 text-xs font-medium text-red-700">
                    ABN needs action
                  </span>
                )}
                {awaitingReplyTotal > 0 && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2.5 py-0.5 text-xs font-medium text-red-700">
                    <NavIcon name="message-square" size={12} />
                    {awaitingReplyTotal} awaiting your reply
                  </span>
                )}
              </div>
              <div className="mt-1 text-sm text-slate-600">
                <span className="font-medium text-slate-700">{company.contact_name}</span>
                <span className="mx-2 text-slate-300">·</span>
                <a
                  href={`mailto:${company.email}`}
                  className="text-slate-500 hover:text-slate-700 hover:underline"
                >
                  {company.email}
                </a>
              </div>
            </div>
          </div>

          {/* Actions, aligned with the company name and avatar. */}
          <div className="flex flex-wrap items-center justify-end gap-2">
            {notifiedAt !== null && pendingChangesCount === 0 && (
              <span className="text-xs text-emerald-600">Changes sent ✓</span>
            )}
            {!isDeactivated && (
              <>
                {/* All lifecycle actions pause while deactivated (the API 409s them
                    anyway — this keeps the UI honest). */}
                <Btn
                  size="sm"
                  variant="info"
                  onClick={() => inviteMutation.mutate()}
                  loading={inviteMutation.isPending}
                >
                  {company.invited_at ? 'Resend invite' : 'Send invite'}
                </Btn>
                {questionnaires.some(isManualSendable) && (
                  <Btn size="sm" variant="info" onClick={() => setSendQOpen(true)}>
                    Send questionnaire
                  </Btn>
                )}
                {pendingChangesCount > 0 && (
                  <Btn
                    size="sm"
                    variant="info"
                    onClick={() => notifyChangesMutation.mutate()}
                    loading={notifyChangesMutation.isPending}
                  >
                    Notify vendor of changes ({pendingChangesCount})
                  </Btn>
                )}
              </>
            )}
            {company.invited_at && (
              <p className="w-full text-right text-xs text-slate-500">
                Last invite sent {formatDate(company.invited_at)}
              </p>
            )}
          </div>
        </div>
      </div>
      {notifyChangesMutation.error && <ErrorMessage error={notifyChangesMutation.error} />}

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <Tabs
          tabs={tabItems}
          active={activeKey}
          onSelect={setActiveTab}
          ariaLabel="Vendor review sections"
          className="px-2 pt-2"
        />
        <div className="p-6">
          {activeGroup ? (
            <QuestionnaireTabPanel
              key={activeGroup.questionnaire.id}
              group={activeGroup}
              invitedAt={company.invited_at}
              visibleIds={progress.visibleIds}
              completionByQuestion={progress.completionByQuestion}
              subByQ={subByQ}
              companyId={id}
              companyName={company.company_name}
              vendorNumber={company.vendor_number}
              documentName={documentName}
              documents={documents}
              questionById={questionById}
            />
          ) : activeKey === COMPLIANCE_DOCS_TAB ? (
            <div
              role="tabpanel"
              id={`tabpanel-${COMPLIANCE_DOCS_TAB}`}
              aria-labelledby={`tab-${COMPLIANCE_DOCS_TAB}`}
            >
              <TabHeading
                title="Compliance Documents"
                subtitle="Every document this vendor needs to provide, across all questionnaires."
                actions={
                  tabModel.allComplianceDocs.length > 0 ? (
                    <Btn size="sm" variant="info" onClick={() => setUploadDocOpen(true)}>
                      <NavIcon name="file-text" size={16} />
                      Upload document
                    </Btn>
                  ) : undefined
                }
              />
              {uploadDocOpen && (
                <AdminUploadDocumentModal
                  companyId={id}
                  rows={tabModel.allComplianceDocs}
                  onClose={() => setUploadDocOpen(false)}
                  onUploaded={() => {
                    setUploadDocOpen(false);
                    void qc.invalidateQueries({ queryKey: ['company', id, 'submissions'] });
                    void qc.invalidateQueries({ queryKey: ['company', id] });
                  }}
                />
              )}
              {tabModel.complianceDocs.length > 0 ? (
                <>
                  <ComplianceDocStats
                    submissions={tabModel.complianceDocs.map((r) => subByQ.get(r.question.id))}
                  />
                  <DocumentReviewTable
                    rows={tabModel.complianceDocs}
                    subByQ={subByQ}
                    companyId={id}
                    documents={documents}
                    documentName={documentName}
                    questionById={questionById}
                  />
                </>
              ) : tabModel.allComplianceDocs.length > 0 ? (
                <p className="text-sm italic text-slate-400">
                  No documents have been marked compliant yet. Documents awaiting review appear
                  under their questionnaire tab.
                </p>
              ) : (
                <p className="text-sm italic text-slate-400">
                  No document requirements for this vendor.
                </p>
              )}
            </div>
          ) : activeKey === ACTIVITY_TAB ? (
            <div
              role="tabpanel"
              id={`tabpanel-${ACTIVITY_TAB}`}
              aria-labelledby={`tab-${ACTIVITY_TAB}`}
            >
              <TabHeading
                title="Activity log"
                subtitle="Everything that has happened to this vendor — admin actions, vendor submissions, and system events."
              />
              <CompanyActivityTab companyId={id} />
            </div>
          ) : activeKey === QUESTIONNAIRES_TAB ? (
            <div
              role="tabpanel"
              id={`tabpanel-${QUESTIONNAIRES_TAB}`}
              aria-labelledby={`tab-${QUESTIONNAIRES_TAB}`}
            >
              {selectedGroupedId === ADDITIONAL_TAB ? (
                <div>
                  <TabHeading
                    title="Additional questions"
                    subtitle="Questions whose category no longer exists."
                  />
                  {/* -mx-4 offsets the rows' own px-4 so text keeps its
                      alignment while highlight bands bleed slightly wider. */}
                  <div className="-mx-4 divide-y divide-slate-200">
                    {tabModel.orphanQuestions.map((question) => (
                      <SubmissionRow
                        key={question.id}
                        question={question}
                        submission={subByQ.get(question.id)}
                        documentName={documentName}
                        companyId={id}
                        documents={documents}
                        instanceOption={instanceOptionLabel(question, questionById)}
                      />
                    ))}
                  </div>
                </div>
              ) : selectedGroupedGroup ? (
                <div>
                  {/* Questionnaire name + review pill as direct flex siblings so
                      the pill centers on the title (margin-box centering issue —
                      see QuestionnaireTabPanel). The panel-level "Questionnaires"
                      heading is intentionally gone: the dropdown already names
                      this tab and the selected questionnaire heads its content. */}
                  <div className="mb-5 flex flex-wrap items-center gap-3">
                    <h2 className="text-xl font-semibold text-slate-900">
                      {selectedGroupedGroup.questionnaire.name}
                    </h2>
                    <QuestionnaireReviewPill group={selectedGroupedGroup} subByQ={subByQ} />
                    <div className="ml-auto">
                      <ExportQuestionnaireButton
                        companyId={id}
                        companyName={company.company_name}
                        vendorNumber={company.vendor_number}
                        group={selectedGroupedGroup}
                        subByQ={subByQ}
                        visibleIds={progress.visibleIds}
                        completionByQuestion={progress.completionByQuestion}
                      />
                    </div>
                  </div>
                  <QuestionnaireStatusStrip
                    progress={questionnaireProgress(
                      selectedGroupedGroup,
                      progress.visibleIds,
                      subByQ,
                      progress.completionByQuestion,
                    )}
                    review={reviewProgress(selectedGroupedGroup, progress.visibleIds, subByQ)}
                    dateLabel="Sent"
                    dateValue={selectedGroupedGroup.questionnaire.sent_at}
                  />
                  <CategorySections
                    sections={selectedGroupedGroup.sections}
                    subByQ={subByQ}
                    companyId={id}
                    documentName={documentName}
                    documents={documents}
                    questionById={questionById}
                  />
                </div>
              ) : (
                <p className="text-sm text-slate-500">
                  Select a questionnaire from the Questionnaires tab dropdown above to view its
                  questions.
                </p>
              )}
            </div>
          ) : (
            <div
              role="tabpanel"
              id={`tabpanel-${COMPANY_INFO_TAB}`}
              aria-labelledby={`tab-${COMPANY_INFO_TAB}`}
            >
              <CompanyInfoPanel company={company} />
            </div>
          )}
        </div>
      </div>

      <div className="border-t border-slate-200 pt-6">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-red-600">Danger zone</h3>
        {isDeactivated ? (
          <div className="mt-3 flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 p-4">
            <div className="text-sm text-slate-700">
              This vendor is deactivated. Reactivate to restore their previous status and portal
              access.
            </div>
            <Btn onClick={() => setConfirmOpen(true)} loading={reactivateMutation.isPending}>
              Reactivate vendor
            </Btn>
          </div>
        ) : (
          <div className="mt-3 flex items-center justify-between rounded-xl border border-red-200 bg-red-50 p-4">
            <div className="text-sm text-red-700">
              Deactivate this vendor. They&apos;ll be hidden from the vendors list and lose portal
              access. Records are kept — you can reactivate them anytime.
            </div>
            <Btn
              variant="danger"
              onClick={() => setConfirmOpen(true)}
              loading={deactivateMutation.isPending}
            >
              Deactivate vendor
            </Btn>
          </div>
        )}
      </div>

      <ConfirmModal
        open={confirmOpen}
        title={isDeactivated ? 'Reactivate vendor' : 'Deactivate vendor'}
        confirmLabel={isDeactivated ? 'Reactivate' : 'Deactivate'}
        danger={!isDeactivated}
        loading={isDeactivated ? reactivateMutation.isPending : deactivateMutation.isPending}
        onConfirm={() =>
          isDeactivated ? reactivateMutation.mutate() : deactivateMutation.mutate()
        }
        onClose={() => {
          setConfirmOpen(false);
          deactivateMutation.reset();
          reactivateMutation.reset();
        }}
      >
        {isDeactivated ? (
          <>
            Are you sure you want to reactivate{' '}
            <span className="font-semibold">{company.company_name}</span>? They&apos;ll return to
            their previous status and regain portal access.
          </>
        ) : (
          <>
            Are you sure you want to deactivate{' '}
            <span className="font-semibold">{company.company_name}</span>? They&apos;ll be removed
            from the vendors list and lose portal access. Their records are kept — you can
            reactivate them anytime.
          </>
        )}
        {(deactivateMutation.error ?? reactivateMutation.error) != null && (
          <div className="mt-3">
            <ErrorMessage error={deactivateMutation.error ?? reactivateMutation.error} />
          </div>
        )}
      </ConfirmModal>

      {sendQOpen && (
        <SendQuestionnaireModal
          companyId={id}
          options={sendOptions}
          onClose={() => setSendQOpen(false)}
          onSent={() => {
            setSendQOpen(false);
            void qc.invalidateQueries({ queryKey: ['company', id, 'questionnaires'] });
          }}
        />
      )}

      <Modal
        open={inviteSentOpen}
        onClose={() => setInviteSentOpen(false)}
        title="Invitation sent"
        size="sm"
        footer={
          <Btn variant="info" onClick={() => setInviteSentOpen(false)}>
            Done
          </Btn>
        }
      >
        <p className="text-sm text-slate-600">
          An invitation email has been sent to {company.email}.
        </p>
        {company.invited_at && (
          <p className="mt-2 text-xs text-slate-500">
            Last invite sent {formatDate(company.invited_at)}
          </p>
        )}
      </Modal>
    </div>
  );
}

// Heading shown at the top of each tab panel. The Company information tab also
// passes a subtitle and panel-level actions (Edit / Save / Cancel); category
// tabs pass the category name as the title only.
function TabHeading({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
}) {
  return (
    <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
      <div>
        <h2 className="text-xl font-semibold text-slate-900">{title}</h2>
        {subtitle && <p className="mt-1 text-sm text-slate-500">{subtitle}</p>}
      </div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </div>
  );
}

// One review row: a type tile, the question with its answer/files, then the
// status and per-item actions on the right. The request-changes form (comment
// + reference doc) lives in a modal.
function SubmissionRow({
  question,
  submission,
  documentName,
  companyId,
  documents,
  instanceOption,
}: {
  question: Question;
  submission: AdminSubmission | undefined;
  documentName: (id: string) => string;
  companyId: string;
  documents: CompanyDocument[];
  instanceOption: string | null;
}) {
  const [rejectOpen, setRejectOpen] = useState(false);
  const [commentOpen, setCommentOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  function closeReject() {
    setRejectOpen(false);
    reviewMutation.reset();
  }

  function closeComment() {
    setCommentOpen(false);
    reviewMutation.reset();
  }

  const reviewMutation = useReviewMutation(companyId, submission?.id, () => {
    closeReject();
    closeComment();
  });

  // Frozen renewal record: once a vendor replaces an APPROVED compliance
  // document, this questionnaire row permanently shows the originally-approved
  // file(s) + an Approved badge and stops being a review surface. The
  // replacement is uploaded, reviewed, and notified exclusively in the
  // Compliance Documents tab (DocumentReviewTable).
  const frozen = frozenApprovedDoc(submission, question.include_in_compliance_documents);
  const files = frozen.frozen ? frozen.files : (submission?.submission_files ?? []);

  // Yellow "needs review" band: an answered question whose next move is the
  // admin's (same isAwaitingAdmin signal as the review bar and the pill), so a
  // reviewer can scan straight to the rows that still need a decision. Never
  // shown on a frozen row — its renewal is reviewed in the Compliance
  // Documents tab, not here.
  const needsReview = submission !== undefined && !frozen.frozen && isAwaitingAdmin(submission);

  // The history modal shows uploaded-file versions + policy-document choices, so
  // only offer it for questions that can carry that history — document/adoption
  // questions (even when all their files were later removed and none are live),
  // never a plain text/select answer.
  const canHaveDocHistory =
    question.type === 'document_upload' ||
    question.type === 'multiselect_uploads' ||
    question.type === 'document_adopt' ||
    question.requires_upload ||
    question.adoptable_doc_id != null;
  const hasDocHistory =
    !!submission &&
    (canHaveDocHistory || files.length > 0 || (submission.submission_adoptions?.length ?? 0) > 0);

  return (
    <div
      className={`flex items-center gap-3 py-3 ${
        needsReview ? 'border-l-4 border-orange-400 bg-amber-50 pl-3 pr-4' : 'px-4'
      }${question.deleted_at ? ' opacity-60' : ''}`}
    >
      <div className="min-w-0 flex-1">
        {submission && (
          <div className="mb-1">
            <SubmissionStatusBadge
              status={
                frozen.frozen
                  ? frozen.status
                  : effectiveDocStatus(
                      submission.status,
                      hasExpiredLiveFile(submission.submission_files, new Date()),
                    )
              }
            />
          </div>
        )}
        <div className="text-sm font-semibold text-slate-900">
          {question.label}
          {question.deleted_at && (
            <span className="ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-normal text-slate-500">
              Deleted
            </span>
          )}
          {instanceOption && (
            <span className="ml-2 rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-normal text-indigo-700">
              For: {instanceOption}
            </span>
          )}
        </div>
        {question.help_text && (
          <div className="mt-0.5 text-xs text-slate-500">{question.help_text}</div>
        )}
        <div className="mt-0.5 flex flex-wrap items-start gap-x-2 gap-y-1 text-sm text-slate-600">
          <AnswerPreview question={question} submission={submission} documentName={documentName} />
          {submission && files.length > 0 && (
            <div className="flex w-full flex-wrap items-start gap-x-2 gap-y-1">
              {files.map((f) => (
                <SubmissionFileItem
                  key={f.id}
                  companyId={companyId}
                  submissionId={submission.id}
                  file={f}
                />
              ))}
            </div>
          )}
        </div>
        {submission?.reviewer_comment && (
          <div className="mt-2 rounded-md border border-orange-200 bg-orange-50 px-3 py-2 text-xs text-orange-800">
            <div className="font-medium">Reviewer note</div>
            <div>{submission.reviewer_comment}</div>
          </div>
        )}
        {/* Approve/Disapprove errors show here; reject errors show inside the modal. */}
        {!rejectOpen && !commentOpen && reviewMutation.error && (
          <div className="mt-2">
            <ErrorMessage error={reviewMutation.error} />
          </div>
        )}
      </div>

      <div className="flex shrink-0 items-center gap-2">
        {hasDocHistory && (
          <Btn
            size="sm"
            variant="secondary"
            aria-label="Document history"
            title="History"
            onClick={() => setHistoryOpen(true)}
          >
            <NavIcon name="clock" size={16} />
          </Btn>
        )}
        {question.exclude_from_compliance ? (
          <span className="text-xs text-slate-500">Not part of compliance — no review needed.</span>
        ) : frozen.frozen ? (
          // Frozen renewal record: no verdict or comment controls here — this
          // row is a permanent snapshot of the original approved answer, and
          // must not become a second review surface for the replacement.
          <span
            className="max-w-56 text-right text-xs text-slate-500"
            title="This row permanently shows the originally approved document. The vendor's replacement is reviewed in the Compliance Documents tab."
          >
            <span className="mr-2 inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 font-medium text-slate-500">
              Approved — original submission
            </span>
            Renewals are managed in the Compliance Documents tab.
          </span>
        ) : (
          submission &&
          (() => {
            const status = submission.status;
            const isApproved = status === 'approved';
            const isRejected = status === 'rejected';
            const isNa = status === 'not_applicable';
            const awaitingReply = awaitingAdminReply(submission);
            // Green count = admin comments the vendor hasn't replied to. When the
            // ball is back in the admin's court (vendor commented last OR
            // resubmitted), reset to 0 so the red "awaiting your reply" dot takes
            // over — the two badges are mutually exclusive.
            const adminComments = awaitingReply ? 0 : (submission.unanswered_admin_comment_count ?? 0);
            const hasComments = adminComments > 0;
            const dec = reviewMutation.variables?.decision;
            const busy = reviewMutation.isPending;
            // Locked once the verdict has been notified to the vendor: the
            // reviewer can no longer change it (the round is preserved as a
            // snapshot). Releases when the vendor reopens the question.
            const locked = submission.review_locked ?? false;
            // Show the spinner on the button that initiated the in-flight call —
            // a reset originates from whichever status is currently active.
            const loadingFor = (target: 'approve' | 'reject' | 'na', active: boolean) =>
              busy && (dec === target || (dec === 'reset' && active));
            return (
              <>
                {locked && (
                  <span
                    className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-500"
                    title="Verdict locked — the vendor has been notified. It reopens if the vendor uploads a new version."
                  >
                    Locked
                  </span>
                )}
                <Btn
                  size="sm"
                  variant={isApproved ? 'success' : 'secondary'}
                  aria-pressed={isApproved}
                  disabled={locked}
                  onClick={() =>
                    reviewMutation.mutate({ decision: isApproved ? 'reset' : 'approve' })
                  }
                  loading={loadingFor('approve', isApproved)}
                >
                  <NavIcon name="check-circle" size={16} />
                  Compliant
                </Btn>
                <Btn
                  size="sm"
                  variant={isRejected ? 'danger' : 'secondary'}
                  aria-pressed={isRejected}
                  disabled={locked}
                  onClick={() =>
                    isRejected ? reviewMutation.mutate({ decision: 'reset' }) : setRejectOpen(true)
                  }
                  loading={loadingFor('reject', isRejected)}
                >
                  <NavIcon name="x-circle" size={16} />
                  Not Compliant
                </Btn>
                <Btn
                  size="sm"
                  variant={isNa ? 'warning' : 'secondary'}
                  aria-pressed={isNa}
                  disabled={locked}
                  onClick={() => reviewMutation.mutate({ decision: isNa ? 'reset' : 'na' })}
                  loading={loadingFor('na', isNa)}
                >
                  N/A
                </Btn>
                <span className="relative inline-flex">
                  <Btn
                    size="sm"
                    variant={hasComments ? 'info' : 'secondary'}
                    aria-label={
                      awaitingReply
                        ? `Vendor replied — awaiting your reply for "${question.label}"`
                        : hasComments
                          ? `Comment added for "${question.label}" — ${adminComments} so far`
                          : `Add comment for "${question.label}"`
                    }
                    onClick={() => setCommentOpen(true)}
                  >
                    <NavIcon name="edit" size={16} />
                    {hasComments ? 'Comment added' : 'Comments'}
                  </Btn>
                  {hasComments && (
                    <span
                      className="pointer-events-none absolute -left-1.5 -top-1.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-emerald-500 px-1 text-[10px] font-semibold leading-none text-white ring-2 ring-white"
                      aria-hidden="true"
                    >
                      {adminComments}
                    </span>
                  )}
                  {awaitingReply && (
                    <span
                      className="pointer-events-none absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-red-500 ring-2 ring-white"
                      aria-hidden="true"
                    />
                  )}
                </span>
              </>
            );
          })()
        )}
      </div>

      {rejectOpen && (
        <NotCompliantModal
          question={question}
          documents={documents}
          pending={reviewMutation.isPending}
          error={reviewMutation.error}
          onClose={closeReject}
          onSubmit={(payload) => reviewMutation.mutate({ decision: 'reject', ...payload })}
        />
      )}

      {commentOpen && (
        <CommentModal
          question={question}
          companyId={companyId}
          submissionId={submission?.id}
          onClose={closeComment}
        />
      )}

      {historyOpen && submission && (
        <FileHistoryModal
          companyId={companyId}
          submissionId={submission.id}
          questionLabel={question.label}
          onClose={() => setHistoryOpen(false)}
        />
      )}
    </div>
  );
}

// All questions of one questionnaire group, used for tab badges. Doc questions
// are included — since PR #113 they stay inline in their category sections and
// are only mirrored (never moved) into the Compliance Documents tab.
function groupQuestions(g: QuestionnaireGroup): Question[] {
  return g.sections.flatMap((s) => s.questions);
}

// The admin's per-questionnaire review pill: derived from this questionnaire's
// submissions (status + who commented last). null when there is nothing to show.
function QuestionnaireReviewPill({
  group,
  subByQ,
}: {
  group: QuestionnaireGroup;
  subByQ: Map<string, AdminSubmission>;
}) {
  const subs = groupQuestions(group)
    .map((q) => subByQ.get(q.id))
    .filter((s): s is AdminSubmission => Boolean(s));
  const label = ADMIN_REVIEW_LABEL[reviewTurn(subs)];
  if (!label) return null;
  return (
    <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-700">
      {label}
    </span>
  );
}

// Progress + date status strip shown above a questionnaire's questions. Shared
// by the own-tab panel (date = company "Invited") and the consolidated
// Questionnaires tab (date = this questionnaire's "Sent" time), so both read
// identically. dateValue is an ISO string or null (rendered as "—").
// One card pairs the vendor's completion bar with the admin's own review bar
// ("N reviewed · M need review") so both sides of the workflow read together.
function QuestionnaireStatusStrip({
  progress,
  review,
  dateLabel,
  dateValue,
}: {
  progress: QuestionnaireProgress;
  review: ReviewProgress;
  dateLabel: string;
  dateValue: string | null;
}) {
  return (
    <div className="mb-5 grid gap-4 lg:grid-cols-3">
      <div className="grid gap-4 rounded-xl border border-slate-200 bg-white p-4 sm:grid-cols-2 sm:divide-x sm:divide-slate-100 lg:col-span-2">
        <div>
          <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
            Vendor progress
          </div>
          <div className="mt-2">
            <ProgressBar value={progress.percent} />
          </div>
          <div className="mt-1 text-xs text-slate-500">
            {progress.completed} / {progress.total} complete
          </div>
        </div>
        <div className="sm:pl-4">
          <div className="text-xs font-medium uppercase tracking-wide text-slate-500 sm:text-right">
            Your review
          </div>
          <div className="mt-2">
            <ProgressBar value={review.percent} color="#16a34a" />
          </div>
          <div className="mt-1 text-xs text-slate-500">
            <span className="font-semibold text-green-700">{review.reviewed}</span> reviewed ·{' '}
            <span className="font-semibold text-slate-700">{review.needReview}</span> need review
          </div>
        </div>
      </div>
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="text-xs font-medium uppercase tracking-wide text-slate-500">{dateLabel}</div>
        <div className="mt-2 text-sm text-slate-700">
          {dateValue ? new Date(dateValue).toLocaleString() : '—'}
        </div>
      </div>
    </div>
  );
}

// Category sections for one questionnaire tab: one bordered card per category
// with a tinted header band (same idiom as the Compliance Documents table
// header), then the standard review rows. Compliance-flagged questions render here too
// while their submission is unapproved; once approved they move to the
// Compliance Documents tab.
function CategorySections({
  sections,
  subByQ,
  companyId,
  documentName,
  documents,
  questionById,
  categoryHeadingLevel: CategoryHeading = 'h3',
}: {
  sections: CategorySection[];
  subByQ: Map<string, AdminSubmission>;
  companyId: string;
  documentName: (id: string) => string;
  documents: CompanyDocument[];
  questionById: Map<string, Question>;
  // 'h4' when nested under a per-questionnaire heading (Questionnaires tab).
  categoryHeadingLevel?: 'h3' | 'h4';
}) {
  return (
    <div className="space-y-4">
      {sections.map((s) => (
        <section key={s.category.id} className="overflow-hidden rounded-lg border border-slate-200">
          <CategoryHeading className="border-b border-slate-200 bg-slate-50 px-4 py-2 text-xs font-medium uppercase tracking-wide text-slate-500">
            {s.category.name}
          </CategoryHeading>
          {/* Rows carry their own px-4 so a needs-review row's amber band can
              run edge-to-edge inside the card. */}
          <div className="divide-y divide-slate-200">
            {s.questions.map((question) => (
              <SubmissionRow
                key={question.id}
                question={question}
                submission={subByQ.get(question.id)}
                documentName={documentName}
                companyId={companyId}
                documents={documents}
                instanceOption={instanceOptionLabel(question, questionById)}
              />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

// One applicable questionnaire's tab panel. Leads with a per-questionnaire
// Progress + Invited header block (counting this questionnaire's visible
// questions, mirroring the vendor view), then presents the questionnaire's categories as a
// horizontal sub-tab bar with the selected category's review rows below —
// replacing the older stacked-cards layout. Sub-tab keys are namespaced with a
// "cat:" prefix so their generated tab/tabpanel ids never collide with the
// outer questionnaire tabs.
function QuestionnaireTabPanel({
  group,
  invitedAt,
  visibleIds,
  completionByQuestion,
  subByQ,
  companyId,
  companyName,
  vendorNumber,
  documentName,
  documents,
  questionById,
}: {
  group: QuestionnaireGroup;
  invitedAt: string | null;
  visibleIds: Set<string>;
  completionByQuestion: Map<string, CompletionReason | null>;
  subByQ: Map<string, AdminSubmission>;
  companyId: string;
  companyName: string;
  vendorNumber: string | null;
  documentName: (id: string) => string;
  documents: CompanyDocument[];
  questionById: Map<string, Question>;
}) {
  const prog = questionnaireProgress(group, visibleIds, subByQ, completionByQuestion);
  const review = reviewProgress(group, visibleIds, subByQ);

  const subTabKey = (categoryId: string) => `cat:${categoryId}`;
  const subTabs: TabItem[] = group.sections.map((s) => ({
    key: subTabKey(s.category.id),
    label: s.category.name,
    badge: outstandingActionCount(s.questions, completionByQuestion, subByQ, { skipFrozen: true }),
  }));

  // Default to the first category; reset when switching questionnaires (a new
  // group remounts this component because activeGroup keys by questionnaire.id
  // via the parent's render — but guard anyway against a stale key).
  const [activeSub, setActiveSub] = useState<string>(subTabs[0]?.key ?? '');
  const activeKey = subTabs.some((t) => t.key === activeSub) ? activeSub : (subTabs[0]?.key ?? '');
  const activeSection = group.sections.find((s) => subTabKey(s.category.id) === activeKey);

  return (
    <div
      role="tabpanel"
      id={`tabpanel-${group.questionnaire.id}`}
      aria-labelledby={`tab-${group.questionnaire.id}`}
    >
      {/* Title and review pill as direct flex siblings so the pill centers on
          the title text. TabHeading carries its own bottom margin, so nesting it
          here would make the pill center on its (taller) margin box and drift
          below the title. */}
      <div className="mb-5 flex flex-wrap items-center gap-3">
        <h2 className="text-xl font-semibold text-slate-900">{group.questionnaire.name}</h2>
        <QuestionnaireReviewPill group={group} subByQ={subByQ} />
        <div className="ml-auto">
          <ExportQuestionnaireButton
            companyId={companyId}
            companyName={companyName}
            vendorNumber={vendorNumber}
            group={group}
            subByQ={subByQ}
            visibleIds={visibleIds}
            completionByQuestion={completionByQuestion}
          />
        </div>
      </div>

      {/* Per-questionnaire status strip: progress scoped to this
          questionnaire's visible questions (vendor-view parity), plus the
          (shared) invite timestamp. */}
      <QuestionnaireStatusStrip
        progress={prog}
        review={review}
        dateLabel="Invited"
        dateValue={invitedAt}
      />

      {/* Categories as sub-tabs (one active category panel at a time). */}
      <Tabs
        tabs={subTabs}
        active={activeKey}
        onSelect={setActiveSub}
        ariaLabel={`${group.questionnaire.name} categories`}
      />
      {activeSection && (
        <div
          role="tabpanel"
          id={`tabpanel-${activeKey}`}
          aria-labelledby={`tab-${activeKey}`}
          className="pt-4"
        >
          {/* -mx-4 offsets the rows' own px-4 (see CategorySections). */}
          <div className="-mx-4 divide-y divide-slate-200">
            {activeSection.questions.map((question) => (
              <SubmissionRow
                key={question.id}
                question={question}
                submission={subByQ.get(question.id)}
                documentName={documentName}
                companyId={companyId}
                documents={documents}
                instanceOption={instanceOptionLabel(question, questionById)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Admin "upload on behalf of vendor" modal for the Compliance Documents tab.
// Requires picking one of the vendor's compliance-doc questions and a file,
// then runs the standard 3-step upload (presign → PUT → register) against the
// admin endpoints and lets the caller invalidate the page queries so the new
// file appears in the review table.
function AdminUploadDocumentModal({
  companyId,
  rows,
  onClose,
  onUploaded,
}: {
  companyId: string;
  rows: ComplianceDocRow[];
  onClose: () => void;
  onUploaded: () => void;
}) {
  const [questionId, setQuestionId] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [expiry, setExpiry] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<unknown>(null);

  // Group the question options by category name for a scannable dropdown; the
  // rows already arrive in questionnaire → category → question order.
  const groups: { categoryName: string; questions: Question[] }[] = [];
  for (const { question, categoryName } of rows) {
    const last = groups[groups.length - 1];
    if (last && last.categoryName === categoryName) last.questions.push(question);
    else groups.push({ categoryName, questions: [question] });
  }

  async function submit() {
    if (!questionId || !file || uploading) return;
    setUploading(true);
    setError(null);
    try {
      const mimeType = file.type || 'application/octet-stream';
      const presigned = await adminApi.post<{ path: string; url: string }>(
        `/api/companies/${companyId}/submissions/${questionId}/upload-url`,
        { file_name: file.name, mime_type: mimeType },
      );
      await uploadFileToSignedUrl(presigned.url, file);
      await adminApi.post(`/api/companies/${companyId}/submissions/${questionId}/files`, {
        file_path: presigned.path,
        file_name: file.name,
        size_bytes: file.size,
        mime_type: mimeType,
        expires_at: fromDateInput(expiry),
      });
      onUploaded();
    } catch (err) {
      setError(err);
    } finally {
      setUploading(false);
    }
  }

  return (
    <Modal
      open
      title="Upload document on behalf of vendor"
      size="md"
      onClose={onClose}
      footer={
        <>
          <Btn size="sm" variant="ghost" onClick={onClose} disabled={uploading}>
            Cancel
          </Btn>
          <Btn
            size="sm"
            onClick={() => void submit()}
            disabled={!questionId || !file}
            loading={uploading}
          >
            Upload
          </Btn>
        </>
      }
    >
      <div className="space-y-3">
        <Field label="Related document requirement" required>
          <Select
            value={questionId}
            onChange={(e) => setQuestionId(e.target.value)}
            disabled={uploading}
          >
            <option value="">— Select a document —</option>
            {groups.map((g) => (
              <optgroup key={g.categoryName} label={g.categoryName}>
                {g.questions.map((q) => (
                  <option key={q.id} value={q.id}>
                    {complianceDocName(q)}
                  </option>
                ))}
              </optgroup>
            ))}
          </Select>
        </Field>
        <Field label="File" required>
          <FileDrop
            multiple={false}
            accept={DOCUMENT_ACCEPT}
            disabled={uploading}
            onFiles={(list) => {
              const f = list[0];
              if (f) setFile(f);
            }}
            hint={file ? `Selected: ${file.name}` : uploading ? 'Uploading…' : undefined}
          />
        </Field>
        <Field label="Expiry date (optional)" hint="Leave blank if this document doesn't expire.">
          <DateField
            value={expiry}
            onChange={setExpiry}
            disabled={uploading}
            ariaLabel="Expiry date"
            placeholder="No expiry"
          />
        </Field>
        {error != null && <ErrorMessage error={error} />}
      </div>
    </Modal>
  );
}

// One submitted file shown as an inline link. Images open in a modal lightbox;
// other file types open in a new tab. The signed URL is fetched on demand.
function SubmissionFileItem({
  companyId,
  submissionId,
  file,
}: {
  companyId: string;
  submissionId: string;
  file: SubmissionFile;
}) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const isImage = file.mime_type.startsWith('image/');
  const downloadPath = `/api/companies/${companyId}/submissions/${submissionId}/files/${file.id}/download-url`;

  // No thumbnail to populate, so the short-lived signed URL is only fetched when
  // the lightbox is actually opened.
  const urlQuery = useQuery({
    queryKey: ['submission-file-url', submissionId, file.id],
    queryFn: () => adminApi.get<{ url: string }>(downloadPath),
    enabled: isImage && lightboxOpen,
    staleTime: 4 * 60 * 1000,
  });

  async function openInNewTab() {
    const url =
      urlQuery.data?.url ??
      (await adminApi.get<{ url: string }>(downloadPath).catch(() => null))?.url;
    if (url) window.open(url, '_blank', 'noopener');
  }

  return (
    <span className="inline-flex items-center gap-1.5">
      <button
        type="button"
        onClick={() => (isImage ? setLightboxOpen(true) : void openInNewTab())}
        className="inline-flex items-center gap-1 text-xs text-[#4D148C] hover:underline"
      >
        <span aria-hidden="true">📎</span>
        {file.file_name}
      </button>
      <ExpiryChip expiresAt={file.expires_at} />

      {isImage && lightboxOpen && (
        <Modal
          open
          title={file.file_name}
          size="lg"
          onClose={() => setLightboxOpen(false)}
          footer={
            <>
              <Btn
                variant="ghost"
                size="sm"
                disabled={!urlQuery.data?.url}
                onClick={() =>
                  urlQuery.data?.url && downloadSignedUrl(urlQuery.data.url, file.file_name)
                }
              >
                Download file
              </Btn>
              <Btn variant="ghost" size="sm" onClick={() => void openInNewTab()}>
                Open in new tab
              </Btn>
              <Btn variant="secondary" size="sm" onClick={() => setLightboxOpen(false)}>
                Close
              </Btn>
            </>
          }
        >
          {urlQuery.data?.url ? (
            <img
              src={urlQuery.data.url}
              alt={file.file_name}
              className="mx-auto max-h-[65vh] max-w-full rounded-md"
            />
          ) : (
            <div className="flex h-40 items-center justify-center text-sm text-slate-400">
              {urlQuery.isError ? 'Image unavailable' : 'Loading…'}
            </div>
          )}
        </Modal>
      )}
    </span>
  );
}

function AnswerPreview({
  question,
  submission,
  documentName,
}: {
  question: Question;
  submission: AdminSubmission | undefined;
  documentName: (id: string) => string;
}) {
  // Document questions are satisfied by an upload or an adoption, not a typed
  // answer, so they must never fall through to a generic "Not answered yet".
  if (question.type === 'document_adopt' || question.type === 'document_upload') {
    const adoptions = submission?.submission_adoptions ?? [];
    if (adoptions.length > 0) {
      return (
        <ul className="space-y-1">
          {adoptions.map((ad) => (
            <li key={ad.id} className="text-sm">
              {ad.mode === 'adopted' ? '✓ Adopted: ' : '↑ Uploaded: '}
              {documentName(ad.document_id)}
            </li>
          ))}
        </ul>
      );
    }
    if (question.type === 'document_upload') {
      // Uploaded file(s) render below this preview with their expiry chip.
      if ((submission?.submission_files?.length ?? 0) > 0) return null;
      return <span className="italic text-slate-400">Awaiting document</span>;
    }
    return <span className="italic text-slate-400">No adoption recorded</span>;
  }

  if (!submission) return <span className="italic text-slate-400">Not answered yet</span>;
  if (submission.in_revision) {
    return <span className="italic text-amber-600">Vendor is revising this answer</span>;
  }
  const a = submission.answer;
  if (question.type === 'signature') {
    if (
      a &&
      typeof a === 'object' &&
      !Array.isArray(a) &&
      typeof (a as { signature_data_url?: unknown }).signature_data_url === 'string'
    ) {
      const sig = a as { signature_data_url: string; signer_name: string; signed_at: string };
      return (
        <div className="space-y-1">
          <img
            src={sig.signature_data_url}
            alt="Signature"
            className="h-24 rounded border border-slate-200 bg-white object-contain"
          />
          <div className="text-sm text-slate-700">
            Signed by <span className="font-medium">{sig.signer_name}</span>
          </div>
          <div className="text-xs text-slate-500">{new Date(sig.signed_at).toLocaleString()}</div>
        </div>
      );
    }
    return <span className="italic text-slate-400">Not signed yet</span>;
  }
  const pills = answerOptionPills(question, a);
  if (pills) return <OptionPills pills={pills} />;
  if (Array.isArray(a)) return <span>{(a as string[]).join(', ')}</span>;
  if (typeof a === 'boolean') return <span>{a ? 'Yes' : 'No'}</span>;
  if (a === null || a === undefined || a === '') {
    return <span className="italic text-slate-400">Not answered yet</span>;
  }
  return <span>{String(a)}</span>;
}

// Read-only pill list for option-based questions: every offered option on its
// own row, with the company's selection(s) tinted in the brand accent (same
// idiom as StatusFilterTabs). Selection is also conveyed by the check glyph
// and sr-only text, not colour alone.
function OptionPills({ pills }: { pills: AnswerOptionPill[] }) {
  return (
    <div className="flex w-full flex-col items-start gap-1.5">
      {pills.map((p, i) => (
        <span
          key={i}
          className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-medium ${
            p.selected ? '' : 'border-slate-200 bg-white text-slate-500'
          }`}
          style={
            p.selected
              ? {
                  color: 'var(--brand-accent)',
                  borderColor: 'var(--brand-accent)',
                  backgroundColor: 'color-mix(in srgb, var(--brand-accent) 12%, white)',
                }
              : undefined
          }
        >
          {p.selected && <span aria-hidden="true">✓</span>}
          {p.label}
          {p.selected && <span className="sr-only">(selected)</span>}
        </span>
      ))}
    </div>
  );
}

// ----------------------------------------------------------------------
// Company information panel. Read-only by default; the panel-level Edit
// button beside the tab heading switches both cards — FedEx Details and
// Vendor Details — to inputs, saved together through a single PATCH.
// ----------------------------------------------------------------------

type CompanyInfoForm = {
  contractor_types: string[];
  vendor_number: string;
  contract_numbers: string[];
  cluster_region: string;
  station: string;
  abn: string;
  legal_entity_name: string;
  address_line: string;
  city: string;
  state: string;
  postcode: string;
  country: string;
  trustee_info: string;
  primary_contact_name: string;
  contact_role: string;
  contact_phone: string;
  contact_email: string;
  employee_count: string;
};

// Every field except employee_count and the two list fields is sent as trimmed
// text (empty → null); employee_count is parsed to an integer and the lists are
// sent as arrays (empty → null) separately in the save mutation.
const infoTextFields = [
  'vendor_number',
  'cluster_region',
  'station',
  'abn',
  'legal_entity_name',
  'address_line',
  'city',
  'state',
  'postcode',
  'country',
  'trustee_info',
  'primary_contact_name',
  'contact_role',
  'contact_phone',
  'contact_email',
] as const;

function toCompanyInfoForm(c: Company): CompanyInfoForm {
  return {
    contractor_types: c.contractor_types ?? [],
    vendor_number: c.vendor_number ?? '',
    contract_numbers: c.contract_numbers ?? [],
    cluster_region: c.cluster_region ?? '',
    station: c.station ?? '',
    abn: c.abn ?? '',
    legal_entity_name: c.legal_entity_name ?? '',
    address_line: c.address_line ?? '',
    city: c.city ?? '',
    state: c.state ?? '',
    postcode: c.postcode ?? '',
    country: c.country ?? '',
    trustee_info: c.trustee_info ?? '',
    primary_contact_name: c.primary_contact_name ?? '',
    contact_role: c.contact_role ?? '',
    contact_phone: c.contact_phone ?? '',
    contact_email: c.contact_email ?? '',
    employee_count: c.employee_count === null ? '' : String(c.employee_count),
  };
}

function CompanyInfoPanel({ company }: { company: Company }) {
  const qc = useQueryClient();
  const { settings } = usePortalSettings();
  const contractorTypes = contractorTypeOptions(settings);
  const clusterRegions = clusterRegionOptions(settings);
  const stations = stationOptions(settings);
  const [form, setForm] = useState<CompanyInfoForm>(() => toCompanyInfoForm(company));
  const [error, setError] = useState<unknown>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  // Both cards start read-only; the Edit button beside the heading switches
  // in the inputs.
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    setForm(toCompanyInfoForm(company));
  }, [company.id, company.updated_at]);

  // Dirty when any field differs from the company record's current values.
  const baseline = toCompanyInfoForm(company);
  const sameList = (a: string[], b: string[]) =>
    a.length === b.length && a.every((v, i) => v === b[i]);
  const dirty =
    infoTextFields.some((key) => form[key] !== baseline[key]) ||
    !sameList(form.contractor_types, baseline.contractor_types) ||
    !sameList(form.contract_numbers, baseline.contract_numbers) ||
    form.employee_count !== baseline.employee_count;

  const saveMutation = useMutation({
    mutationFn: () => {
      const payload: Record<string, string | string[] | number | null> = {};
      for (const key of infoTextFields) {
        const trimmed = form[key].trim();
        payload[key] = trimmed.length === 0 ? null : trimmed;
      }
      // Empty list clears the field (NULL), mirroring the blank-text convention.
      payload.contractor_types = form.contractor_types.length === 0 ? null : form.contractor_types;
      payload.contract_numbers = form.contract_numbers.length === 0 ? null : form.contract_numbers;
      // Mirrors the company portal's handling: blank clears the value, anything
      // else must parse to a non-negative integer (sync throws land in onError).
      const empCount = form.employee_count.trim();
      if (empCount === '') {
        payload.employee_count = null;
      } else {
        const n = Number.parseInt(empCount, 10);
        if (!Number.isFinite(n) || n < 0) {
          throw new Error('# of employees must be a non-negative number');
        }
        payload.employee_count = n;
      }
      return adminApi.patch<{ company: Company }>(`/api/companies/${company.id}`, payload);
    },
    onSuccess: () => {
      setError(null);
      setSavedAt(Date.now());
      setEditing(false);
      void qc.invalidateQueries({ queryKey: ['company', company.id] });
    },
    onError: (err) => setError(err),
  });

  const abnCheck = useAbnVerification(editing ? form.abn : '', 'admin');

  function set<K extends keyof CompanyInfoForm>(key: K, value: CompanyInfoForm[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  // Auto-fill registered details from a verified ABN. Smart-sync: fills when
  // blank or still holding our own prior fill; never clobbers a manual edit.
  const abnFill = abnCheck.state === 'verified' ? abnCheck.result : null;
  useAutoFillField(abnFill?.entityName ?? null, form.legal_entity_name, (v) =>
    set('legal_entity_name', v),
  );
  useAutoFillField(abnFill?.addressState ?? null, form.state, (v) => set('state', v));
  useAutoFillField(abnFill?.addressPostcode ?? null, form.postcode, (v) => set('postcode', v));

  // Cancel discards in-progress edits, clears any error, and returns to the
  // read-only view.
  function cancelEdit() {
    setForm(toCompanyInfoForm(company));
    setError(null);
    saveMutation.reset();
    setEditing(false);
  }

  // MultiSelect only surfaces the options it's handed and rebuilds the
  // selection from them, so fold in any saved contractor types that are no
  // longer configured in Settings — otherwise editing the field would hide
  // them and silently drop them on save. Configured options first, then any
  // such legacy values, mirroring the OptionChecklist ordering this replaced.
  const contractorTypeOptionsWithSaved = [
    ...contractorTypes,
    ...form.contractor_types.filter((t) => !contractorTypes.includes(t)),
  ];

  return (
    <div>
      {/* Heading row carries the panel-level controls: Edit when read-only,
          Cancel/Save while editing. One Save writes both cards' fields. */}
      <TabHeading
        title="Vendor information"
        subtitle="FedEx details you manage and information the vendor provided."
        actions={
          editing ? (
            <>
              <Btn
                size="sm"
                variant="secondary"
                onClick={cancelEdit}
                disabled={saveMutation.isPending}
              >
                Cancel
              </Btn>
              <Btn
                size="sm"
                onClick={() => saveMutation.mutate()}
                loading={saveMutation.isPending}
                disabled={!dirty}
              >
                Save
              </Btn>
            </>
          ) : (
            <>
              {savedAt !== null && <span className="text-xs text-emerald-600">Saved</span>}
              <Btn size="sm" variant="info" onClick={() => setEditing(true)}>
                Edit
              </Btn>
            </>
          )
        }
      />

      {error != null && (
        <div className="mb-4">
          <ErrorMessage error={error} />
        </div>
      )}

      <div className="grid items-start gap-4 md:grid-cols-2">
        {/* FedEx Details — admin-managed fields. */}
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <h3 className="mb-1 text-base font-semibold text-slate-800">FedEx Details</h3>
          {editing ? (
            <div>
              <InfoRow label={companyInfoLabel(settings, 'contractorType')}>
                {/* Dropdown multi-select matching the create-vendor form. Options
                    include any saved types no longer in Settings so they stay
                    visible and removable rather than being silently dropped. */}
                <MultiSelect
                  label={companyInfoLabel(settings, 'contractorType')}
                  values={contractorTypeOptionsWithSaved}
                  selected={form.contractor_types}
                  onChange={(next) => set('contractor_types', next)}
                />
              </InfoRow>
              <InfoRow
                label={companyInfoLabel(settings, 'vendorNumber')}
                htmlFor="company-vendor-number"
              >
                <TextInput
                  id="company-vendor-number"
                  value={form.vendor_number}
                  onChange={(e) => set('vendor_number', e.target.value)}
                />
              </InfoRow>
              <InfoRow label={companyInfoLabel(settings, 'contractNumber')}>
                <StringListEditor
                  value={form.contract_numbers}
                  onChange={(list) => set('contract_numbers', list)}
                  itemLabel="contract number"
                  addLabel="Add contract number"
                  maxLength={255}
                />
              </InfoRow>
              <InfoRow
                label={companyInfoLabel(settings, 'clusterRegion')}
                htmlFor="company-cluster-region"
              >
                <Select
                  id="company-cluster-region"
                  value={form.cluster_region}
                  onChange={(e) => set('cluster_region', e.target.value)}
                  disabled={clusterRegions.length === 0}
                >
                  <option value="">— Select —</option>
                  {clusterRegions.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </Select>
                {clusterRegions.length === 0 && (
                  <p className="mt-1 text-xs text-slate-500">
                    Add cluster regions in Settings to populate this dropdown.
                  </p>
                )}
              </InfoRow>
              <InfoRow label={companyInfoLabel(settings, 'station')} htmlFor="company-station">
                <Select
                  id="company-station"
                  value={form.station}
                  onChange={(e) => set('station', e.target.value)}
                  disabled={stations.length === 0}
                >
                  <option value="">— Select —</option>
                  {stations.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </Select>
                {stations.length === 0 && (
                  <p className="mt-1 text-xs text-slate-500">
                    Add stations in Settings to populate this dropdown.
                  </p>
                )}
              </InfoRow>
            </div>
          ) : (
            <div>
              <InfoRow label={companyInfoLabel(settings, 'contractorType')}>
                <ReadOnlyValue
                  value={
                    company.contractor_types && company.contractor_types.length > 0
                      ? company.contractor_types.join(', ')
                      : null
                  }
                />
              </InfoRow>
              <InfoRow label={companyInfoLabel(settings, 'vendorNumber')}>
                <ReadOnlyValue value={company.vendor_number} />
              </InfoRow>
              <InfoRow label={companyInfoLabel(settings, 'contractNumber')}>
                <ReadOnlyValue
                  value={
                    company.contract_numbers && company.contract_numbers.length > 0
                      ? company.contract_numbers.join(', ')
                      : null
                  }
                />
              </InfoRow>
              <InfoRow label={companyInfoLabel(settings, 'clusterRegion')}>
                <ReadOnlyValue value={company.cluster_region} />
              </InfoRow>
              <InfoRow label={companyInfoLabel(settings, 'station')}>
                <ReadOnlyValue value={company.station} />
              </InfoRow>
            </div>
          )}
        </div>

        {/* Vendor Details — values the vendor submitted, editable here too so
            an admin can correct them on the vendor's behalf. */}
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <h3 className="mb-1 text-base font-semibold text-slate-800">Vendor Details</h3>
          {editing ? (
            <div>
              <InfoRow label="ABN" htmlFor="company-abn">
                <TextInput
                  id="company-abn"
                  value={form.abn}
                  onChange={(e) => set('abn', e.target.value)}
                />
                <AbnStatusIndicator state={abnCheck.state} result={abnCheck.result} />
              </InfoRow>
              <InfoRow label="Trustee Name and ACN" htmlFor="company-trustee-info">
                <TextInput
                  id="company-trustee-info"
                  value={form.trustee_info}
                  onChange={(e) => set('trustee_info', e.target.value)}
                />
              </InfoRow>
              <InfoRow label="Legal entity name" htmlFor="company-legal-entity-name">
                <TextInput
                  id="company-legal-entity-name"
                  value={form.legal_entity_name}
                  onChange={(e) => set('legal_entity_name', e.target.value)}
                />
              </InfoRow>
              <InfoRow label="Address" htmlFor="company-address-line">
                <TextInput
                  id="company-address-line"
                  value={form.address_line}
                  onChange={(e) => set('address_line', e.target.value)}
                />
              </InfoRow>
              <InfoRow label="City" htmlFor="company-city">
                <TextInput
                  id="company-city"
                  value={form.city}
                  onChange={(e) => set('city', e.target.value)}
                />
              </InfoRow>
              <InfoRow label="State" htmlFor="company-state">
                <TextInput
                  id="company-state"
                  value={form.state}
                  onChange={(e) => set('state', e.target.value)}
                />
              </InfoRow>
              <InfoRow label="Postcode" htmlFor="company-postcode">
                <TextInput
                  id="company-postcode"
                  value={form.postcode}
                  onChange={(e) => set('postcode', e.target.value)}
                />
              </InfoRow>
              <InfoRow label="Country" htmlFor="company-country">
                <TextInput
                  id="company-country"
                  value={form.country}
                  onChange={(e) => set('country', e.target.value)}
                />
              </InfoRow>
              <InfoRow
                label={companyInfoLabel(settings, 'primaryContact')}
                htmlFor="company-primary-contact"
              >
                <TextInput
                  id="company-primary-contact"
                  value={form.primary_contact_name}
                  onChange={(e) => set('primary_contact_name', e.target.value)}
                />
              </InfoRow>
              <InfoRow label="Contact role/position" htmlFor="company-contact-role">
                <TextInput
                  id="company-contact-role"
                  value={form.contact_role}
                  onChange={(e) => set('contact_role', e.target.value)}
                />
              </InfoRow>
              <InfoRow label="Contact phone" htmlFor="company-contact-phone">
                <TextInput
                  id="company-contact-phone"
                  type="tel"
                  value={form.contact_phone}
                  onChange={(e) => set('contact_phone', e.target.value)}
                />
              </InfoRow>
              <InfoRow label="Contact email" htmlFor="company-contact-email">
                <TextInput
                  id="company-contact-email"
                  type="email"
                  value={form.contact_email}
                  onChange={(e) => set('contact_email', e.target.value)}
                />
              </InfoRow>
              <InfoRow label="# of employees" htmlFor="company-employee-count">
                <TextInput
                  id="company-employee-count"
                  type="number"
                  min={0}
                  value={form.employee_count}
                  onChange={(e) => set('employee_count', e.target.value)}
                />
              </InfoRow>
            </div>
          ) : (
            <div>
              <InfoRow label="ABN">
                <ReadOnlyValue value={company.abn} />
                {abnNeedsAction(company.abn_verification_status) && (
                  <div className="mt-1">
                    <span className="inline-flex items-center rounded-full bg-red-50 px-2.5 py-0.5 text-xs font-medium text-red-700">
                      ABN needs action
                    </span>
                  </div>
                )}
                <div className="mt-0.5">
                  <AbnVerifiedBadge
                    status={company.abn_verification_status}
                    entityName={company.abn_entity_name}
                    registryStatus={company.abn_registry_status}
                  />
                </div>
              </InfoRow>
              <InfoRow label="ABN registry status">
                <ReadOnlyValue value={company.abn_registry_status} />
              </InfoRow>
              <InfoRow label="Trustee Name and ACN">
                <ReadOnlyValue value={company.trustee_info} />
              </InfoRow>
              <InfoRow label="Legal entity name">
                <ReadOnlyValue value={company.legal_entity_name} />
              </InfoRow>
              <InfoRow label="Address">
                <ReadOnlyValue value={company.address_line} />
              </InfoRow>
              <InfoRow label="City">
                <ReadOnlyValue value={company.city} />
              </InfoRow>
              <InfoRow label="State">
                <ReadOnlyValue value={company.state} />
              </InfoRow>
              <InfoRow label="Postcode">
                <ReadOnlyValue value={company.postcode} />
              </InfoRow>
              <InfoRow label="Country">
                <ReadOnlyValue value={company.country} />
              </InfoRow>
              <InfoRow label={companyInfoLabel(settings, 'primaryContact')}>
                <ReadOnlyValue value={company.primary_contact_name} />
              </InfoRow>
              <InfoRow label="Contact role/position">
                <ReadOnlyValue value={company.contact_role} />
              </InfoRow>
              <InfoRow label="Contact phone">
                <ReadOnlyValue value={company.contact_phone} />
              </InfoRow>
              <InfoRow label="Contact email">
                <ReadOnlyValue value={company.contact_email} />
              </InfoRow>
              <InfoRow label="# of employees">
                <ReadOnlyValue
                  value={company.employee_count === null ? null : String(company.employee_count)}
                />
              </InfoRow>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// One label/value row: label in a fixed-ish left column, arbitrary content
// (a read-only value or an input) on the right, with a divider between rows.
// Stacks on narrow screens; two columns at sm and up.
function InfoRow({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor?: string;
  children: ReactNode;
}) {
  return (
    <div
      className="grid grid-cols-1 gap-1 border-b border-slate-100 py-3 last:border-b-0
                 sm:grid-cols-[minmax(160px,32%)_1fr] sm:items-center sm:gap-4"
    >
      {htmlFor ? (
        <label htmlFor={htmlFor} className="text-sm text-slate-500">
          {label}
        </label>
      ) : (
        <div className="text-sm text-slate-500">{label}</div>
      )}
      <div className="text-sm">{children}</div>
    </div>
  );
}

// Read-only value for the right column of an InfoRow: bold when present, an
// italic "Not provided" placeholder when empty.
function ReadOnlyValue({ value }: { value: string | null }) {
  return value && value.trim().length > 0 ? (
    <span className="font-medium text-slate-900">{value}</span>
  ) : (
    <span className="italic text-slate-400">Not provided</span>
  );
}

function SendQuestionnaireModal({
  companyId,
  options,
  onClose,
  onSent,
}: {
  companyId: string;
  options: SendOption[];
  onClose: () => void;
  onSent: () => void;
}) {
  const [selectedId, setSelectedId] = useState(firstSendableId(options));
  const [notify, setNotify] = useState(true);
  const [error, setError] = useState<unknown>(null);

  const sendMutation = useMutation({
    mutationFn: () =>
      adminApi.post(`/api/companies/${companyId}/questionnaires/${selectedId}/send`, { notify }),
    onSuccess: onSent,
    onError: setError,
  });

  const selected = options.find((q) => q.id === selectedId);
  const { verb, alreadySent } = describeSend(selected);
  const fmtDate = (iso: string) =>
    new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });

  return (
    <Modal
      open
      onClose={onClose}
      title={`${verb} questionnaire`}
      size="sm"
      footer={
        <>
          <Btn variant="ghost" onClick={onClose}>
            Cancel
          </Btn>
          <Btn
            onClick={() => sendMutation.mutate()}
            loading={sendMutation.isPending}
            disabled={!selectedId || (selected ? isResendBlocked(selected) : true)}
          >
            {verb}
          </Btn>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Questionnaire" required>
          <Select value={selectedId} onChange={(e) => setSelectedId(e.target.value)}>
            {options.map((q) => (
              <option key={q.id} value={q.id} disabled={isResendBlocked(q)}>
                {optionLabel(q, fmtDate)}
              </option>
            ))}
          </Select>
          {selectedId === '' && options.length > 0 && (
            <p className="text-xs text-slate-500">
              These questionnaires are already compliant — there is nothing to resend.
            </p>
          )}
        </Field>
        {alreadySent && selected?.sent_at && (
          <p className="text-xs text-slate-500">
            Already sent on {fmtDate(selected.sent_at)}. Sending again re-notifies the vendor; it
            won&rsquo;t change their compliance status.
          </p>
        )}
        <label className="inline-flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} />
          Notify vendor by email
        </label>
        <ErrorMessage error={error} />
      </div>
    </Modal>
  );
}
